import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import { interval, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

export interface IAuctionStatusDetails {
  auctionStatus: number;
  auctionControlMethod?: number;
  startingIn: any[];
  startingInFormat: string;
  isStartingIn: boolean;
  closingIn: any[];
  closingInFormat: string;
  isClosingIn: boolean;
  isInPast: boolean;
  startInMoreThatDay: boolean;
  endInMoreThatDay: boolean;
}

export const AUCTION_CONTROL_MANUAL = 0
export const AUCTION_CONTROL_AUTO = 1

export const AuctionStatusBiddingOpen = 0
export const AuctionStatusBiddingPaused = 1
export const AuctionStatusFinalized = 2
export const OnlineBiddingNotEnabled = 3
export const OnlineBiddingTypeLive = 4


@Injectable({
  providedIn: 'root'
})
export class AuctionHelperService {
  public auctionControlMethod = 0;

  public startingIn$: Observable<any> = null;
  public startingIn: any[] = [];
  public startingInFormat$: Observable<any> = null;
  public startingInFormat = '';
  public isStartingIn$: Observable<any> = null;
  public isStartingIn = false;

  public closingIn$: Observable<any> = null;
  public closingIn: any[] = [];
  public closingInFormat$: Observable<any> = null;
  public closingInFormat = '';
  public isClosingIn$: Observable<any> = null;
  public isClosingIn = false;
  public isInPast = false;

  public segments: any[] = [{
    label: 'days',
    duration: 60 * 60 * 24,
    required: false
  }, {
    label: 'hours',
    duration: 60 * 60,
    required: false
  }, {
    label: 'minutes',
    duration: 60,
    required: true
  }, {
    label: 'seconds',
    duration: 1,
    required: true
  }]
  public takeUntilSubject: Subject<any> = new Subject();

  public startDateTime: any = null;
  public endDateTime: any = null;

  public value$: Observable<number> = interval(1000).pipe(
    map(() => {
      return moment().unix();
    })
  );

  constructor() {
  }

  public _transformSegments(timestamp) {
    let display = false;
    return _.chain(this.segments).map(segment => {
      const remainder = timestamp % segment.duration;
      const value = (timestamp - remainder) / segment.duration;

      timestamp = remainder;
      display = (value > 0) || segment.required || display;
      return { ...segment, display, value };
    }).filter(segment => {
      return segment.display;
    }).value();
  }

  public _transformFormat(datetime, timestamp) {
    const seconds = datetime.unix() - timestamp;
    if (seconds < 0) {
      return null;
    }
    if (seconds < 60 * 60 * 24) {
      const resultValue = this._transformSegments(seconds).map(({ value }) => {
        return this.convertToArray(value).join('');
      }).join(':');
      return `${resultValue}`;
    }
    const value = datetime.format('Do MMMM YYYY, hh:mm A');
    return `${value}`;
  }

  public _displaySegments(segments) {
    const sumBy = _.sumBy(segments, segment => {
      return segment.duration * segment.value;
    })
    return sumBy > 0;
  }

  public convertToArray(num: number): string[] {
    const str = num.toString();
    const result = str.length < 2 ? `0${str}` : str;
    return result.split('');
  }

  public getAuctionsStatusDetails(campaign): Observable<IAuctionStatusDetails> {
    return this.value$.pipe(
      map(timestamp => {
        let startInMoreThatDay = false;
        let endInMoreThatDay = false;

        this.auctionControlMethod = _.get(campaign.campaignTypeData, 'auctionControlMethod', 0);

        if (campaign.campaignTypeData.auctionStartTime) {
          this.startDateTime = moment(campaign.campaignTypeData.auctionStartTime * 1000);

          let startingIn = this.startDateTime.unix() - timestamp;
          startingIn = startingIn > 0 ? startingIn : 0;

          this.startingIn = this._transformSegments(startingIn);
          this.startingInFormat = this._transformFormat(this.startDateTime, timestamp);
          this.isStartingIn = this._displaySegments(this.startingIn);

          startInMoreThatDay = !!(this.startingIn && this.startingIn.find(date => date.label === 'days')) || false;
        }

        if (campaign.campaignTypeData.auctionEndTime) {
          this.endDateTime = moment(campaign.campaignTypeData.auctionEndTime * 1000);
          let closingIn = this.endDateTime.unix() - timestamp;
          closingIn = closingIn > 0 ? closingIn : 0;
          this.isInPast = (this.endDateTime.unix() - timestamp < 0)

          this.closingIn = this._transformSegments(closingIn);
          this.closingInFormat = this._transformFormat(this.endDateTime, timestamp);
          this.isClosingIn = !this.isStartingIn && this._displaySegments(this.closingIn);
          startInMoreThatDay = !!(this.startingIn.length && this.startingIn.find(date => date.label === 'days')) || false;
          endInMoreThatDay = !!(this.closingIn.length &&  this.closingIn.find(date => date.label === 'days')) || false;
        }

        const response: IAuctionStatusDetails = {
          auctionControlMethod: this.auctionControlMethod,
          startingIn: this.startingIn,
          startingInFormat: this.startingInFormat,
          isStartingIn: this.isStartingIn,
          closingIn: this.closingIn,
          closingInFormat: this.closingInFormat,
          isClosingIn: this.isClosingIn,
          isInPast: this.isInPast,
          auctionStatus: campaign.status,
          startInMoreThatDay,
          endInMoreThatDay,
        } as IAuctionStatusDetails;

        return response;
      }),
      takeUntil(this.takeUntilSubject)
    );
  }

  public getManualAuctionStatus(campaign, auctionStatusDetails) {
    let status = '';
    let auctionState = ''

    if (auctionStatusDetails.auctionControlMethod === AUCTION_CONTROL_MANUAL) {
      if (campaign.status === AuctionStatusBiddingOpen) {
        status = 'Running';
        auctionState = 'Active';
      } else if (campaign.status === AuctionStatusBiddingPaused) {
        status = 'Bidding not available';
        auctionState = 'Paused';
      } else if (campaign.status === AuctionStatusFinalized) {
        status = 'Auction is already closed.';
        auctionState = 'Done';
      }
    }

    if (campaign.status === AuctionStatusFinalized) {
      status = 'Auction is already closed.';
      auctionState = 'Done';
      return { status, auctionState };
    }

    if (auctionStatusDetails.auctionControlMethod === AUCTION_CONTROL_AUTO) {
      if ((!auctionStatusDetails.isStartingIn && auctionStatusDetails.closingInFormat)
          || (auctionStatusDetails.closingInFormat && auctionStatusDetails.isClosingIn)
          || (!auctionStatusDetails.isStartingIn && !auctionStatusDetails.isClosingIn && !auctionStatusDetails.isInPast)) {
        status = 'Running';
        auctionState = 'Active';
      } else if ((!auctionStatusDetails.isStartingIn && auctionStatusDetails.isInPast)
          || (!auctionStatusDetails.isStartingIn && !auctionStatusDetails.isClosingIn && auctionStatusDetails.isInPast)) {
        status = 'Bidding has ended';
        auctionState = 'Paused';
      } else if (auctionStatusDetails.isStartingIn && auctionStatusDetails.startingInFormat) {
        status = 'Bidding not available';
        auctionState = 'Paused';
      }
    }

    return {status, auctionState};
  }

  public getStatusString(statusObject) {
    if (statusObject.auctionControlMethod === 1) {
      if (statusObject.startingInFormat) {
        return `Auction starts ${ statusObject.startInMoreThatDay ? 'on ' : 'in ' }  ${statusObject.startingInFormat}`;
      }

      if (statusObject.closingInFormat) {
        return `Auction ends ${ statusObject.endInMoreThatDay ? 'on ' : 'in ' }  ${statusObject.closingInFormat}`;
      }
    }
  }

  isCampaignExpaired(auctionData) {
    return +new Date() > +new Date(auctionData.endDate);
  }

  getAuctionStatus(auctionData) {
    const AuctionStatusBiddingOpen = 0;
    const AuctionStatusBiddingPaused = 1;
    const AuctionStatusFinalized = 2;
    const OnlineBiddingTypeLive = 4;

    const AUCTION_CONTROL_MANUAL = 0;

    if (auctionData.auctionType === 1) {
      return OnlineBiddingTypeLive;
    }

    if (auctionData.auctionStatus === AuctionStatusFinalized) {
      return AuctionStatusFinalized;
    }

    if (auctionData.auctionControlMethod === AUCTION_CONTROL_MANUAL) {
      return auctionData.auctionStatus;
    }

    let status = AuctionStatusBiddingOpen;
    const auctionStartTime = auctionData.auctionStartTime;
    const auctionEndTime = auctionData.auctionEndTime;
    const timeNow = moment().unix();

    if (auctionStartTime > timeNow) {
      status = AuctionStatusBiddingPaused;

    } else if (auctionEndTime < timeNow && auctionEndTime !== 0) {
      status = AuctionStatusBiddingPaused;
    } else {
      status = AuctionStatusBiddingOpen;
    }

    return status;
  }

  getAuctionButtonsStatus(campaign, option) {
    return this.getAuctionBidButtonStatus(campaign, option) || 
    this.getAuctionBuyItNowButtonStatus(campaign, option) || 
    this.getSoldOutBadgeStatus(campaign, option);
  }

  getAuctionBidButtonStatus(campaign, option) {
    const auctionStatus = this.getAuctionStatus(campaign.campaignTypeData);

    let status = true;

    if (auctionStatus === OnlineBiddingNotEnabled) {
      status = false;
    } else if (auctionStatus !== AuctionStatusBiddingOpen) {
      status = false;
    } else if (auctionStatus === AuctionStatusBiddingOpen
      && option.count <= option.soldCount) {
      status = false;
    }

    if (option.buyItNowOnlyItem) {
      status = false;
    }

    return status;
  }

  getAuctionBuyItNowButtonStatus(campaign, option) {
    const AuctionStatusBiddingOpen = 0;
    const AuctionStatusFinalized = 2;
    const auctionStatus = this.getAuctionStatus(campaign.campaignTypeData);

    let status = true;
    if (auctionStatus !== AuctionStatusBiddingOpen) {
      status = false;
    }
    if (auctionStatus === AuctionStatusFinalized) {
      status = true;
    }

    if (option.count <= option.soldCount) {
      status = false;
    }

    return status;
  }

  getSoldOutBadgeStatus(campaign, option) {
    const auctionStatus = this.getAuctionStatus(campaign.campaignTypeData);
    
    if (auctionStatus === OnlineBiddingNotEnabled) {
      return false;
    } else if(auctionStatus === AuctionStatusBiddingOpen 
      && option.count <= option.soldCount) {
      return true;
    }
    return false;
  }

  showBuyItNow(campaign, bid): boolean {
    if (bid && bid.buyItNowEnabled
      && (bid.buyItNowAmount > 0)
      && (bid.count - bid.soldCount > 0)) {
      return [0, 2].includes(campaign.status);
    } else {
      return false;
    }
  }

  showBidButton(campaign, bid): boolean {
    if (bid && (bid.count - bid.soldCount > 0) && !bid.buyItNowOnlyItem) {
      return campaign.status === 0;
    } else {
      return false;
    }
  }


}
